import {
  DropdownItem,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  useFavoriteSecurities,
  useMixpanel,
  type MixpanelEventSource,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';

const FavoriteDropdownItemWrapper = styled.div<{ isFavorite?: boolean }>`
  ${Icon} {
    color: ${({ theme, isFavorite }) => (isFavorite ? theme.colors.yellow.lighten : 'inherit')};
  }
`;

interface FavoriteSymbolDropdownItemProps {
  security: Security | undefined;
  source?: MixpanelEventSource;
  onAfterAction?: () => void;
}

export const FavoriteSymbolDropdownItem = memo(function FavoriteSymbolDropdownItem({
  security,
  source,
  onAfterAction,
}: FavoriteSymbolDropdownItemProps) {
  const mixpanel = useMixpanel();
  const { favoriteSecurities, setFavoriteSecurities } = useFavoriteSecurities();
  const isFavorite = useMemo(
    () => (security ? favoriteSecurities.includes(security.Symbol) : false),
    [security, favoriteSecurities]
  );

  const handleToggleFavorite = useCallback(() => {
    if (!security) {
      return;
    }
    const newFavorites = isFavorite
      ? favoriteSecurities.filter(s => s !== security.Symbol)
      : [...favoriteSecurities, security.Symbol];
    setFavoriteSecurities(newFavorites);
    mixpanel.track(
      MixpanelEvent.FavoriteSymbol,
      source != null ? { [MixpanelEventProperty.Source]: source } : undefined
    );
    onAfterAction?.();
  }, [security, isFavorite, favoriteSecurities, setFavoriteSecurities, mixpanel, onAfterAction, source]);

  if (!security) {
    return null;
  }

  return (
    <FavoriteDropdownItemWrapper isFavorite={isFavorite}>
      <DropdownItem startIcon={isFavorite ? IconName.StarSolid : IconName.Star} onClick={handleToggleFavorite}>
        Favorite Symbol
      </DropdownItem>
    </FavoriteDropdownItemWrapper>
  );
});
